import { useEffect, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import {
  MessageCircle,
  Lock,
  CheckCircle2,
  Clock,
  Loader2,
  FolderOpen,
  CalendarClock,
} from "lucide-react";
import { List, Badge, Avatar, springs } from "@/components/ui";
import { useList } from "@/components/ui/List/ListContext";
import { TagBadge } from "@/components/ui/Tag/TagBadge";
import { useTranslation } from "@audit-app/utils";
import { FileIcon } from "./FileIcon";
import { getCategoryLucideIcon } from "./category-icon-map";
import { statusToBadge, documentStatusLabelKey } from "./constants";
import { useDocumentCategoryLabel } from "./use-document-category-label";
import type { DocumentView } from "./types";
import { DocumentRowMenu } from "./DocumentRowMenu";
import { isExpired } from "./expiration-utils";

interface DocumentListViewProps {
  groupedDocuments?: Map<string, DocumentView[]> | null;
  documents?: DocumentView[];
  onRowClick: (id: string) => void;
  onSelectionChange?: (selectedIds: Set<string>) => void;
  onTagClick?: (docId: string) => void;
  onDownload?: (docId: string) => void;
  onDelete?: (docId: string) => void;
}

function ChecklistMini({ doc }: { doc: DocumentView }) {
  const checklist = doc.checklist;
  if (!checklist || checklist.length === 0) return null;

  const done = checklist.filter((c) => c.done).length;
  const total = checklist.length;
  const progress = (done / total) * 100;

  return (
    <div className="flex items-center gap-1.5 w-24">
      <CheckCircle2 size={12} className="text-muted shrink-0" />
      <div className="flex-1 h-1 bg-border/50 rounded-full overflow-hidden">
        <motion.div
          className="h-full rounded-full"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ ...springs.smooth, delay: 0.4 }}
          style={{
            backgroundColor:
              progress === 100
                ? "#22c55e"
                : progress >= 50
                  ? "#E8862A"
                  : "#a1a1a6",
          }}
        />
      </div>
      <span className="text-[10px] text-muted font-medium shrink-0">
        {done}/{total}
      </span>
    </div>
  );
}

function ExpirationIndicator({
  timestamp,
  t,
}: {
  timestamp: number;
  t: import("i18next").TFunction<["documents"]>;
}) {
  const now = Date.now();
  const daysUntil = Math.ceil((timestamp - now) / (1000 * 60 * 60 * 24));

  let colorClass: string;
  if (daysUntil <= 0) {
    colorClass = "text-red-400 bg-red-500/10 border-red-500/20";
  } else if (daysUntil <= 7) {
    colorClass = "text-red-400 bg-red-500/10 border-red-500/20";
  } else if (daysUntil <= 30) {
    colorClass = "text-amber-400 bg-amber-500/10 border-amber-500/20";
  } else {
    colorClass = "text-muted bg-card border-border/50";
  }

  const label =
    daysUntil <= 0
      ? t("documents:documentGridCard.expired")
      : daysUntil === 1
        ? t("documents:documentGridCard.dayLeft")
        : t("documents:documentGridCard.daysLeft", { days: daysUntil });

  return (
    <span
      className={`hidden sm:inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium border ${colorClass}`}
    >
      <CalendarClock size={10} className="shrink-0" />
      {label}
    </span>
  );
}

interface DocumentRowContentProps {
  doc: DocumentView;
  index: number;
  onTagClick?: (docId: string) => void;
  onDownload?: (docId: string) => void;
  onDelete?: (docId: string) => void;
}

function DocumentRowContent({
  doc,
  index,
  onTagClick,
  onDownload,
  onDelete,
}: DocumentRowContentProps) {
  const { t } = useTranslation(["documents"]);
  const author = doc.author ?? {
    name: t("documents:documentGridCard.unknown"),
  };
  const isGenerating =
    doc.isProcessing || doc.aiSummary?.status === "generating";
  const hasActions = onDownload || onDelete || onTagClick;
  const expired = isExpired(doc.expirationDateTimestamp);

  return (
    <motion.div
      className="flex items-center gap-3 w-full"
      initial={{ opacity: 0, x: -4 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{
        ...springs.snappy,
        delay: Math.min(index, 16) * 0.03,
      }}
    >
      {/* File icon */}
      <div className="shrink-0">
        <FileIcon type={doc.type} />
      </div>

      {/* Content area */}
      <div className="flex-1 min-w-0">
        {/* Line 1: title + expired badge if expired */}
        <div className="flex items-center gap-1.5 min-w-0">
          <p className="text-sm font-semibold text-primary truncate min-w-0">
            {doc.title}
          </p>
          {expired && doc.expirationDateTimestamp && (
            <ExpirationIndicator
              timestamp={doc.expirationDateTimestamp}
              t={t}
            />
          )}
        </div>

        {/* Line 2: date + badges + tags + expiration + checklist */}
        <div className="flex items-center gap-2 mt-0.5 min-w-0">
          <span className="flex items-center gap-1 text-[11px] text-muted shrink-0">
            <Clock size={10} className="shrink-0" />
            {doc.updatedAt}
          </span>
          <span className="text-[11px] text-muted shrink-0">&mdash;</span>

          <div className="flex items-center gap-1.5 shrink-0">
            <Badge variant="status" status={statusToBadge[doc.status]}>
              {doc.status === "under-review" && (
                <motion.span
                  className="inline-block w-1.5 h-1.5 rounded-full bg-current mr-1 align-middle"
                  animate={{ opacity: [1, 0.3, 1] }}
                  transition={{
                    duration: 2,
                    repeat: Number.POSITIVE_INFINITY,
                    ease: "easeInOut",
                  }}
                />
              )}
              {t(documentStatusLabelKey[doc.status])}
            </Badge>

            {isGenerating && (
              <Loader2
                size={12}
                className="animate-spin text-accent shrink-0"
              />
            )}
          </div>

          {/* Tags — visible from sm+ */}
          {doc.tags && doc.tags.length > 0 && (
            <div className="hidden sm:flex items-center gap-1 shrink-0">
              {doc.tags.slice(0, 2).map((tag) => (
                <TagBadge
                  key={tag.id}
                  name={tag.name}
                  color={tag.color}
                  onClick={() => onTagClick?.(doc.id)}
                />
              ))}
              {doc.tags.length > 2 && (
                <span className="text-[10px] text-muted font-medium">
                  +{doc.tags.length - 2}
                </span>
              )}
            </div>
          )}

          {doc.expirationDateTimestamp && !expired && (
            <ExpirationIndicator
              timestamp={doc.expirationDateTimestamp}
              t={t}
            />
          )}

          {/* Checklist mini — visible from md+ */}
          <div className="hidden md:block shrink-0">
            <ChecklistMini doc={doc} />
          </div>

          {doc.confidential && (
            <Lock size={12} className="hidden md:block text-muted shrink-0" />
          )}

          {doc.commentCount != null && doc.commentCount > 0 && (
            <span className="hidden md:flex items-center gap-1 text-[11px] text-secondary shrink-0">
              <MessageCircle size={12} className="shrink-0" />
              {doc.commentCount}
            </span>
          )}
        </div>
      </div>

      {/* Author — fixed column, md+ */}
      <div className="hidden md:flex items-center gap-1.5 shrink-0 w-32">
        <Avatar
          name={author.name}
          avatarColor={author.avatarColor}
          avatarUrl={author.avatarUrl}
          size="xs"
        />
        <span className="text-[11px] text-muted truncate">{author.name}</span>
      </div>

      {/* Actions menu — always visible when callbacks exist */}
      {hasActions && (
        <div className="shrink-0">
          <DocumentRowMenu
            documentId={doc.id}
            documentTitle={doc.title}
            onDownload={() => onDownload?.(doc.id)}
            onDelete={() => onDelete?.(doc.id)}
            onEditTags={() => onTagClick?.(doc.id)}
          />
        </div>
      )}
    </motion.div>
  );
}

function SelectAllRow({
  totalCount,
  allIds,
}: {
  totalCount: number;
  allIds: string[];
}) {
  const { t } = useTranslation(["documents"]);
  const { state, actions } = useList();
  const checkboxRef = useRef<HTMLInputElement>(null);

  const checkedCount = allIds.filter((id) => state.selectedIds.has(id)).length;
  const allChecked = checkedCount === totalCount && totalCount > 0;
  const indeterminate = checkedCount > 0 && !allChecked;

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  const handleChange = () => {
    if (allChecked) {
      actions.deselectAll();
    } else {
      actions.selectAll(allIds);
    }
  };

  return (
    <div className="flex items-center gap-2 px-3 py-2 text-sm text-secondary">
      <input
        ref={checkboxRef}
        type="checkbox"
        checked={allChecked}
        onChange={handleChange}
        className="hidden sm:block h-4 w-4 shrink-0 rounded border border-border checked:bg-accent checked:border-accent"
        style={{ accentColor: "var(--color-accent)" }}
      />
      <span className="font-medium">
        {t("documents:list.selectAll", { count: totalCount })}
      </span>
    </div>
  );
}

export function DocumentListView({
  groupedDocuments,
  documents,
  onRowClick,
  onSelectionChange,
  onTagClick,
  onDownload,
  onDelete,
}: DocumentListViewProps) {
  const getCategoryLabel = useDocumentCategoryLabel();
  // Render grouped mode if groupedDocuments is provided and not null
  const isGroupedMode = groupedDocuments != null;

  const allDocIds = useMemo(() => {
    if (!groupedDocuments) return [];
    return Array.from(groupedDocuments.values())
      .flat()
      .map((d) => d.id);
  }, [groupedDocuments]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={springs.snappy}
      className="h-full overflow-y-auto slim-scroll"
    >
      <List.Provider
        onRowClick={onRowClick}
        onSelectionChange={onSelectionChange}
      >
        <List>
          {isGroupedMode
            ? // Grouped mode: render documents organized by category
              (() => {
                const groups = Array.from(groupedDocuments.entries()).map(
                  ([categoryKey, docs]) => {
                    const firstDoc = docs[0];
                    const iconName = firstDoc?.categoryIcon ?? "folder";
                    const groupTitle = firstDoc?.categoryId
                      ? getCategoryLabel({
                          name: firstDoc.categoryName ?? "",
                          slug: firstDoc.categorySlug,
                        })
                      : getCategoryLabel({
                          name: "Uncategorized",
                          slug: "uncategorized",
                        });
                    const IconComponent =
                      getCategoryLucideIcon(iconName) ?? FolderOpen;
                    const docIds = docs.map((doc) => doc.id);

                    return (
                      <List.Group
                        key={categoryKey}
                        title={groupTitle}
                        icon={<IconComponent className="h-3.5 w-3.5" />}
                        count={docs.length}
                        itemIds={docIds}
                      >
                        {docs.map((doc, i) => (
                          <List.Row key={doc.id} id={doc.id}>
                            <DocumentRowContent
                              doc={doc}
                              index={i}
                              onTagClick={onTagClick}
                              onDownload={onDownload}
                              onDelete={onDelete}
                            />
                          </List.Row>
                        ))}
                      </List.Group>
                    );
                  },
                );
                return (
                  <>
                    <SelectAllRow
                      totalCount={allDocIds.length}
                      allIds={allDocIds}
                    />
                    {groups}
                  </>
                );
              })()
            : // Ungrouped mode: render flat list of documents
              documents?.map((doc, i) => (
                <List.Row key={doc.id} id={doc.id}>
                  <DocumentRowContent
                    doc={doc}
                    index={i}
                    onTagClick={onTagClick}
                    onDownload={onDownload}
                    onDelete={onDelete}
                  />
                </List.Row>
              ))}
        </List>
      </List.Provider>
    </motion.div>
  );
}
